import { forwardRef } from "react";
import { Link } from "react-router";

import {
  Button,
  Card,
  CircularProgress,
  LinearProgress,
  Tab,
  TabProps,
  Tabs,
  TabsProps,
  Typography,
} from "@mui/material";

import { UserRole } from "@models/UserRole";
import isMobile from "@utils/mobileDetect";
import { cn, twc } from "@utils/tailwind";
import useRoleIs from "@utils/useRoleIs";

/**
 * @deprecated use Button / LoadingButton instead
 */

export const StyledButton = ({
  outlined = false,
  cta = false,
  danger = false,
  loading = false,
  disabled = false,
  ...props
}) => (
  <Button
    {...(props.to && { component: Link })}
    disabled={loading || disabled}
    {...props}
    {...(loading && { endIcon: <CircularProgress size={16} /> })}
    className={cn(
      "relative px-4 no-underline transition-all duration-200 !bg-white shadow-sm text-neutral-700 hover:shadow-md hover:!bg-white",
      disabled && "shadow-none text-neutral-500",
      cta &&
        "text-white !bg-gradient-to-r from-neutral-800 to-neutral-700 hover:!bg-neutral-500",
      cta &&
        (disabled || loading) &&
        "!from-neutral-400 !to-neutral-400 !text-neutral-100",
      outlined &&
        "border border-solid shadow-none border-neutral-300 hover:border-neutral-500",
      outlined && disabled && "border-neutral-200 !bg-transparent",
      danger &&
        "border border-solid border-transparent shadow-none hover:text-red-600 hover:border-red-400",
      props.className
    )}
  />
);

export const Contained = twc.div`px-[var(--padding-around)] mx-auto`;

export const OpaqueCard = twc(Card)(() => [
  "rounded-lg bg-white shadow-sm p-6 overflow-hidden",
  isMobile && "px-4 -mx-2 md:mx-0",
]) as unknown as typeof Card;

export const RecessCard = twc.div`rounded-lg bg-neutral-200 p-6 shadow-inner text-neutral-500`;

export const FixedHeightScrollLastChild = (props) => (
  <Contained
    {...props}
    className={cn(
      "space-y-6 pb-[var(--padding-around)]",
      "body-height-after-header",
      props.className
    )}
  />
);

// Typography
export const SectionTitle = twc.div`uppercase text-sm text-primary-600 tracking-widest mb-2 font-bold`;
export const PageTitle = twc(
  Typography
)`font-medium text-neutral-700 text-lg md:text-xl lg:text-2xl max-w-prose`;

export const TableLoading = ({ isNextLoading }) => (
  <div className="w-full h-1 absolute bottom-0 left-0">
    {isNextLoading && <LinearProgress />}
  </div>
);

export const UglyTabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ className, classes, ...props }, ref) => {
    const {
      root: rootClassName,
      indicator: indicatorClassName,
      ...slotClasses
    } = classes ?? {};

    return (
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        ref={ref}
        {...props}
        className={cn(
          "min-h-0 rounded-lg bg-white shadow-sm",
          "[&_.MuiTab-root]:relative [&_.MuiTab-root]:z-10 [&_.MuiTab-root]:min-h-0 [&_.MuiTab-root]:py-3 [&_.MuiTab-root]:transition",
          "[&_.Mui-selected]:!text-white [&_.Mui-selected]:delay-300",
          rootClassName,
          className
        )}
        classes={{
          ...slotClasses,
          indicator: cn(
            "top-0 bottom-0 h-auto rounded-lg z-0",
            indicatorClassName
          ),
        }}
      />
    );
  }
);

UglyTabs.displayName = "UglyTabs";

type UglyTabProps = TabProps & {
  allow?: UserRole[];
  deny?: UserRole[];
  hide?: boolean;
};

export const UglyTab = forwardRef<HTMLAnchorElement, UglyTabProps>(
  ({ allow, deny, hide, ...props }, ref) => {
    const roleIs = useRoleIs();
    const hidden =
      hide || !((!allow?.length || roleIs(allow)) && !roleIs(deny ?? []));
    return (
      <Tab
        ref={ref}
        component={Link}
        {...props}
        className={cn(hidden && "hidden", props.className)}
      />
    );
  }
);
export const PaperTabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ className, classes, style, ...props }, ref) => {
    const {
      root: rootClassName,
      indicator: indicatorClassName,
      ...slotClasses
    } = classes ?? {};

    return (
      <Tabs
        ref={ref}
        {...props}
        className={cn(
          "bg-neutral-50",
          "[&_.MuiTab-root]:min-w-40 [&_.MuiTab-root]:justify-end [&_.MuiTab-root]:rounded-t-lg [&_.MuiTab-root]:px-6 [&_.MuiTab-root]:pb-2 [&_.MuiTab-root]:text-lg [&_.MuiTab-root]:font-medium [&_.MuiTab-root]:normal-case",
          "[&_.Mui-selected]:bg-white [&_.Mui-selected]:shadow-md",
          rootClassName,
          className
        )}
        classes={{
          ...slotClasses,
          indicator: cn("hidden", indicatorClassName),
        }}
        style={{
          boxShadow: "0 -3px 3px 0 rgb(0 0 0 / 0.02) inset",
          ...style,
        }}
      />
    );
  }
);

PaperTabs.displayName = "PaperTabs";

export const DashboardGrid = twc.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-6`;
